import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Calendar, Globe } from "lucide-react";

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function LocalTimeCard({ location, timezone }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Use browser's local time - this is the user's actual time
  const localTime = currentTime;
  
  const hours = localTime.getHours();
  const minutes = localTime.getMinutes();
  const seconds = localTime.getSeconds();
  
  const formattedTime = localTime.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
  
  const formattedDate = localTime.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });

  // Get timezone abbreviation
  const timezoneAbbr = localTime.toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ").pop();

  // Calculate clock hand rotations
  const secondDeg = (seconds / 60) * 360;
  const minuteDeg = ((minutes + seconds / 60) / 60) * 360;
  const hourDeg = ((hours % 12 + minutes / 60) / 12) * 360;

  // Determine if it's day or night for styling
  const isDaytime = hours >= 6 && hours < 18;

  return (
    <motion.div 
      variants={item}
      className="col-span-1 row-span-1 glass rounded-3xl p-5 relative overflow-hidden"
      data-testid="local-time-card"
    >
      <div className={`absolute top-0 right-0 w-32 h-32 ${isDaytime ? 'bg-[#ccff00]/10' : 'bg-[#7000ff]/10'} rounded-full blur-2xl pointer-events-none`} />
      
      <div className="flex items-center gap-2 mb-3">
        <Clock className={`w-4 h-4 ${isDaytime ? 'text-[#ccff00]' : 'text-[#7000ff]'}`} />
        <h3 className="font-semibold text-white/80">Your Time</h3>
      </div>
      
      <div className="flex items-center justify-between">
        {/* Analog Clock */}
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-2 border-white/10 bg-black/30">
            {/* Hour markers */}
            {[...Array(12)].map((_, i) => (
              <div
                key={`hour-marker-${i * 30}`}
                className="absolute w-0.5 h-2 bg-white/30 left-1/2 -translate-x-1/2"
                style={{
                  transformOrigin: "bottom center",
                  transform: `translateX(-50%) rotate(${i * 30}deg)`,
                  top: "4px"
                }}
              />
            ))}
            
            {/* Hour hand */}
            <div
              className="absolute w-1 h-5 bg-white rounded-full left-1/2 bottom-1/2 origin-bottom"
              style={{ transform: `translateX(-50%) rotate(${hourDeg}deg)` }}
            />
            
            {/* Minute hand */}
            <div
              className={`absolute w-0.5 h-7 ${isDaytime ? 'bg-[#ccff00]' : 'bg-[#7000ff]'} rounded-full left-1/2 bottom-1/2 origin-bottom`}
              style={{ transform: `translateX(-50%) rotate(${minuteDeg}deg)` }}
            />
            
            {/* Second hand */}
            <div
              className="absolute w-px h-8 bg-[#ff0080] rounded-full left-1/2 bottom-1/2 origin-bottom"
              style={{ transform: `translateX(-50%) rotate(${secondDeg}deg)` }}
            />
            
            {/* Center dot */}
            <div className="absolute w-2 h-2 bg-white rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
        </div>
        
        {/* Digital Time */}
        <div className="text-right">
          <p className="text-3xl font-black font-mono tracking-tight">
            {formattedTime}
          </p>
          <p className="text-sm text-white/50 flex items-center justify-end gap-1 mt-1">
            <Calendar className="w-3 h-3" />
            {formattedDate}
          </p>
          <p className="text-xs text-white/30 flex items-center justify-end gap-1 mt-1">
            <Globe className="w-3 h-3" />
            {timezoneAbbr}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
